// Story 1.20 — Article 15 export section on the GdprPage.
//
// Renders one of four states:
//   * Idle (no active request): button to request a new export.
//   * In progress (pending/processing): spinner + estimated time + polls.
//   * Completed: download link + bundle size + expiry.
//   * Failed: error message + retry button.
//   * Expired: re-request button.

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@greenhold/components";
import toast from "react-hot-toast";

import { useDsarStatus } from "./useDsarStatus";
import { useExportRequest } from "./useExportRequest";
import { useRequestDataExport } from "./useRequestDataExport";

export function DataExportSection() {
  const dsar = useDsarStatus();
  const activeId = dsar.data?.data.export.active_request_id ?? null;
  const exportReq = useExportRequest(activeId);
  const mutate = useRequestDataExport();

  const handleRequest = () => {
    mutate.mutate(undefined, {
      onSuccess: () => toast.success("Export requested. Check back in a few minutes."),
      onError: (e) => toast.error(e.message),
    });
  };

  const current = exportReq.data?.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Download my data (Article 15)</CardTitle>
        <CardDescription>
          A ZIP bundle containing all data Greenhold holds about you:
          profile, sessions, audit log, ledger entries, and more. The
          bundle is compiled in the background and delivered via a signed
          link valid for 7 days.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!activeId && !current && (
          <Button
            variant="primary"
            onClick={handleRequest}
            disabled={mutate.isPending}
          >
            {mutate.isPending ? "Requesting…" : "Request data export"}
          </Button>
        )}

        {current?.status === "pending" || current?.status === "processing" ? (
          <p className="text-sm opacity-80">
            Your export is being compiled (status: <strong>{current.status}</strong>).
            This page checks for updates every 10 seconds.
          </p>
        ) : null}

        {current?.status === "completed" && current.download_url ? (
          <div className="space-y-2">
            <p className="text-sm">
              Bundle ready ({formatBytes(current.bundle_size_bytes ?? 0)}).
            </p>
            <Button variant="primary" asChild>
              <a href={current.download_url} download>
                Download bundle
              </a>
            </Button>
            {current.download_url_expires_at && (
              <p className="text-xs opacity-70">
                Link expires {new Date(current.download_url_expires_at).toLocaleString()}.
              </p>
            )}
          </div>
        ) : null}

        {current?.status === "failed" && (
          <div className="space-y-2">
            <p className="text-sm text-red-700">
              Compilation failed: {current.failure_message || "unknown error"}.
            </p>
            <Button variant="secondary" onClick={handleRequest}>
              Try again
            </Button>
          </div>
        )}

        {current?.status === "expired" && (
          <div className="space-y-2">
            <p className="text-sm">
              Download link expired. Request a fresh export.
            </p>
            <Button variant="primary" onClick={handleRequest}>
              Re-request
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
